import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ValueMetricsProductType } from '../../../../constants-types';
import {
    ValueMetricLimit,
    SoftwareModuleAccessConfiguration,
    ModuleAccessConfiguration,
    ModuleName,
    ModuleAccountType
} from '../../../../core-data-model';
import { TranslationService } from '../../../../translations';
import { AllowedAccessLevels } from '../../account-subscription-manager-constants';

@Component({
    selector: 'subscription-plans',
    template: `
        <div class="subscription-plan-title">
            {{
                TR.key('subscription-manager.overview-subscription-plan-header')
            }}
        </div>
        <dl>
            <dd class="subscription-plan">
                <ul>
                    @for (plan of subscriptionPlans; track plan) {
                        <li>
                            {{ plan }}
                        </li>
                    }
                </ul>
            </dd>
        </dl>
    `,
    styleUrls: [
        './subscription-plans.component.less',
        '../overview.component.less'
    ],
    standalone: false
})
export class SubscriptionPlansComponent implements OnChanges {
    @Input()
    softwareModuleAccessConfig: SoftwareModuleAccessConfiguration;
    @Input()
    vmConfigs: ValueMetricLimit[] = [];
    subscriptionPlans: string[] = [];

    private readonly DisplayedSubscriptionModules: string[] = [
        ModuleName.HireHero,
        ModuleName.Design,
        ModuleName.Design2,
        ModuleName.Inspire2,
        ModuleName.Diagnose2
    ];
    private readonly AllowedAccountTypes: ModuleAccountType[] = [
        ModuleAccountType.Free,
        ModuleAccountType.Paid
    ];

    private readonly SubscriptionModulesDisplayMap: { [key: string]: string } =
        {
            [ModuleName.HireHero]: this.TR.key(
                'subscription-manager.overview-module-hire'
            ),
            [ModuleName.Design]: this.TR.key(
                'subscription-manager.overview-module-design'
            ),
            [ModuleName.Design2]: this.TR.key(
                'subscription-manager.overview-module-design'
            ),
            [ModuleName.Diagnose2]: this.TR.key(
                'subscription-manager.overview-module-diagnose'
            ),
            [ModuleName.Inspire2]: this.TR.key(
                'subscription-manager.overview-module-inspire'
            )
        };

    private readonly AccountTypesMap: { [key: number]: string } = {
        [ModuleAccountType.Free]: this.TR.key(
            'subscription-manager.overview-account-type-free'
        ),
        [ModuleAccountType.Paid]: this.TR.key(
            'subscription-manager.overview-account-type-paid'
        )
    };

    constructor(public TR: TranslationService) {}

    async ngOnChanges(changes: SimpleChanges) {
        this.subscriptionPlans = this.transformSMACToSubscriptionPlans(
            this.softwareModuleAccessConfig,
            this.vmConfigs
        );
    }

    public transformSMACToSubscriptionPlans(
        server: SoftwareModuleAccessConfiguration,
        vmConfigs: ValueMetricLimit[]
    ): string[] {
        const filteredModuleAccessConfigs =
            this.filterModuleAccessConfigurations(
                server.softwareModuleAccessConfig
            );
        return this.mapModuleAccessConfigurationsToPlanStrings(
            filteredModuleAccessConfigs,
            vmConfigs
        );
    }

    private filterModuleAccessConfigurations(
        configs: ModuleAccessConfiguration[]
    ): ModuleAccessConfiguration[] {
        return configs.filter(
            (value) =>
                this.DisplayedSubscriptionModules.some(
                    (moduleName) => moduleName === value.name
                ) &&
                this.AllowedAccountTypes.some(
                    (accountType) => accountType === value.moduleAccountType
                ) &&
                AllowedAccessLevels.some(
                    (accessLevel) => accessLevel === value.accessLevel
                )
        );
    }

    private mapModuleAccessConfigurationsToPlanStrings(
        configs: ModuleAccessConfiguration[],
        vmConfigs: ValueMetricLimit[]
    ): string[] {
        return configs.map((value) => {
            const planTypeString =
                this.AccountTypesMap[value.moduleAccountType];
            const planNameString =
                this.SubscriptionModulesDisplayMap[value.name];
            const vmDetails = vmConfigs.find(
                (x) => ValueMetricsProductType[x.product] === value.name
            );
            const unlimitedString =
                vmDetails !== undefined && vmDetails.limit === -1
                    ? this.TR.key(
                          'subscription-manager.overview-subscription-plan-unlimited'
                      )
                    : '';
            return `${planNameString} ${planTypeString} ${unlimitedString}`.toUpperCase();
        });
    }
}
